<template>
  <button
    v-if="overlay"
    type="button"
    class="wishlist-overlay"
    :class="{ 'wishlist-overlay--active': action === 'remove' }"
    :aria-label="label"
    @click="toggleWishList"
  >
    <img :src="inlineIconSrc" :alt="label" class="wishlist-overlay__icon" />
  </button>
  <button
    v-else-if="inline"
    type="button"
    class="wishlist-inline"
    :class="{
      'wishlist-inline--active': action === 'remove',
      'wishlist-inline--plain': useBlackIcon,
    }"
    :aria-label="label"
    @click="toggleWishList"
  >
    <span v-if="!useBlackIcon" class="wishlist-inline__divider" />
    <img
      :src="inlineIconSrc"
      alt="Add to wishlist"
      class="wishlist-inline__icon"
    />
  </button>
  <button
    v-else-if="combined"
    type="button"
    class="wishlist-combined"
    @click="toggleWishList"
  >
    <span class="wishlist-combined__label">{{ label }}</span>
    <span class="wishlist-combined__divider" />
    <img
      :src="inlineIconSrc"
      alt="Add to wishlist"
      class="wishlist-combined__icon"
    />
  </button>
  <Button v-else width="full" theme="primary" @click="toggleWishList">
    {{ label }}
  </Button>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import Button from "@/components/ButtonV3.vue";
import bookmarkOutlineIcon from "@/assets/icons/bookmark.png";
import bookmarkFilledIcon from "@/assets/icons/bookmark-filled.png";
import bookmarkBlackIcon from "@/assets/icons/bookmark-black.png";
import bookmarkBlackFilledIcon from "@/assets/icons/bookmark-black-filled.png";

export default {
  name: "WishlistButton",
  components: {
    Button,
  },
  inject: ["mq"],
  props: {
    product: {
      type: Object,
      required: true,
    },
    variant: {
      type: Object,
      required: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    useBlackIcon: {
      type: Boolean,
      default: false,
    },
    combined: {
      type: Boolean,
      default: false,
    },
    overlay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      action: "add",
      bookmarkOutlineIcon,
      bookmarkFilledIcon,
    };
  },
  computed: {
    ...mapState("auth", ["customer"]),
    ...mapGetters("auth", ["isLoggedIn", "wishlist"]),
    strippedProductVariantId() {
      return this.variant?.id.split("gid://shopify/ProductVariant/")[1];
    },
    label() {
      return this.action === "add" ? "ADD TO WISHLIST" : "REMOVE FROM WISHLIST";
    },
    isActive() {
      return this.action === "remove";
    },
    inlineIconSrc() {
      if (this.useBlackIcon || this.overlay) {
        return this.isActive ? bookmarkBlackFilledIcon : bookmarkBlackIcon;
      }

      return this.isActive ? bookmarkFilledIcon : bookmarkOutlineIcon;
    },
  },
  watch: {
    wishlist() {
      this.checkIfProductVariantInWishlist();
    },
    variant() {
      this.checkIfProductVariantInWishlist();
    },
  },
  mounted() {
    this.checkIfProductVariantInWishlist();
  },
  methods: {
    ...mapActions("app", ["setWishlistLoginOpenState", "addItemToWishlist"]),
    async toggleWishList(event) {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (!this.isLoggedIn) {
        this.action = "remove";
        this.addProductToGuestWishlist();
        this.setWishlistLoginOpenState(true);
        return;
      }

      const oldAction = this.action;
      this.action = oldAction === "add" ? "remove" : "add";

      await this.addItemToWishlist({
        productVariantId: this.variant.id,
        action: oldAction.toLowerCase(),
        product: this.product,
      });

      this.checkIfProductVariantInWishlist();
    },
    addProductToGuestWishlist() {
      localStorage.setItem("ratandboaGuestWishlist", this.variant.id);
    },
    isPendingGuestWishlistItem() {
      const guestId = localStorage.getItem("ratandboaGuestWishlist");
      return (
        guestId === this.variant?.id ||
        guestId === this.strippedProductVariantId
      );
    },
    checkIfProductVariantInWishlist() {
      const pending = this.isPendingGuestWishlistItem();

      if (!this.isLoggedIn) {
        if (pending) {
          this.action = "remove";
        }
        return false;
      }

      const localWishlist = localStorage.getItem("ratandboaWishlist");

      if (localWishlist) {
        const wishlists = JSON.parse(localWishlist);
        const wishlist = wishlists[0];
        const inWishlist = wishlist?.productVariants?.includes(
          this.strippedProductVariantId
        );
        this.action = inWishlist || pending ? "remove" : "add";
      } else {
        this.action = pending ? "remove" : "add";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.button {
  background-color: $color-grey-light;
  color: $color-black;
  border: none;
}

.wishlist-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $color-black;
  border: none;
  padding: 0 18px;
  cursor: pointer;
  border-radius: 0 4px 4px 0;
  outline: none;
}

.wishlist-inline--plain {
  background-color: transparent;
  border-radius: 0;
  padding: 0;
}

.wishlist-inline__divider {
  width: 1px;
  height: 60%;
  max-height: 26px;
  background-color: $color-white;
  opacity: 0.8;
  margin-right: 16px;
}

.wishlist-inline__icon {
  width: 10px;
  height: 14px;
  display: block;
}

.wishlist-combined {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 15px 18px 15px 28px;
  background-color: $color-black;
  color: $color-white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
  @include utility-button(13px);
}

.wishlist-combined__label {
  flex: 1;
  text-align: center;
}

.wishlist-combined__divider {
  width: 1px;
  height: 60%;
  max-height: 26px;
  background-color: $color-white;
  opacity: 0.8;
  margin: 0 16px;
}

.wishlist-combined__icon {
  width: 10px;
  height: 14px;
  display: block;
  flex-shrink: 0;
}

.wishlist-overlay {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: 19px;
  padding: 0 5px;
  background: transparent;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  outline: none;

  @include respond-above(md) {
    height: 21px;
    padding: 0 10px;
  }

  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    background: transparent;
  }
}

.wishlist-overlay__icon {
  width: auto;
  height: 14px;
  display: block;

  @include respond-above(md) {
    height: 16px;
  }
}
</style>
